import {
  STATUS,
  isInFlight,
  isRetryable,
  isSettling,
} from "../../domain/mediaUpload";

/* Recorder phases describe the camera, upload statuses the transfer. The dialog
   needs one answer, and this is where the two are reconciled. */
export const PHASE = Object.freeze({
  IDLE: "idle",
  COUNTDOWN: "countdown",
  RECORDING: "recording",
  PREVIEW: "preview",
  SENDING: "sending",
  SETTLING: "settling",
  DONE: "done",
  ERROR: "error",
});

/** The upload wins once it starts: the camera is no longer what is being waited on. */
export const viewPhase = (recorderPhase, uploadStatus) => {
  if (uploadStatus === STATUS.FAILED) return PHASE.ERROR;
  if (uploadStatus === STATUS.COMPLETE) return PHASE.DONE;
  if (isInFlight(uploadStatus)) return PHASE.SENDING;
  if (isSettling(uploadStatus)) return PHASE.SETTLING;

  return recorderPhase === "error" ? PHASE.ERROR : recorderPhase;
};

// Buttons as data: the two flows differ only by the config passed in, so adding
// an affordance is a row here rather than a branch in two components.
const action = (key, label, extra = {}) => ({ key, label, ...extra });

const CANCEL = action("cancel", "Cancel", { variant: "text" });

const UPLOAD = action("selectFile", "Upload", {
  variant: "outlined",
  icon: "upload",
});

const capture = ({ canUpload }) =>
  [
    CANCEL,
    canUpload && UPLOAD,
    action("record", "Record", { icon: "record" }),
  ].filter(Boolean);

const recording = (phase) => () =>
  [
    action("stop", phase === PHASE.COUNTDOWN ? "Cancel" : "Stop", {
      variant: "tonal",
      icon: "stop",
    }),
  ];

const preview = ({ submitLabel }) => [
  CANCEL,
  action("retry", "Retry", { variant: "outlined", icon: "restart" }),
  action("submit", submitLabel),
];

const sending = ({ canCancelSend }) =>
  canCancelSend
    ? [action("cancelSend", "Cancel Upload", { variant: "outlined" })]
    : [];

// Nothing to do but wait or walk away — the upload continues either way.
const settling = ({ canCloseWhileSettling }) =>
  canCloseWhileSettling ? [action("close", "Close", { variant: "text" })] : [];

const errored = ({ upload, canUpload }) =>
  [
    CANCEL,
    isRetryable(upload) &&
      action("retrySend", "Try again", {
        variant: "outlined",
        icon: "restart",
      }),
    canUpload && UPLOAD,
    action("record", "Record again", { icon: "record" }),
  ].filter(Boolean);

const BY_PHASE = {
  [PHASE.IDLE]: capture,
  [PHASE.COUNTDOWN]: recording(PHASE.COUNTDOWN),
  [PHASE.RECORDING]: recording(PHASE.RECORDING),
  [PHASE.PREVIEW]: preview,
  [PHASE.SENDING]: sending,
  [PHASE.SETTLING]: settling,
  [PHASE.DONE]: () => [],
  [PHASE.ERROR]: errored,
};

export const DEFAULT_CONFIG = Object.freeze({
  canUpload: true,
  canCancelSend: true,
  canCloseWhileSettling: true,
  submitLabel: "Send",
  upload: Object.freeze({ status: STATUS.IDLE, errorClassification: null }),
});

const withoutUndefined = (config) =>
  Object.fromEntries(
    Object.entries(config).filter(([, value]) => value !== undefined),
  );

export const actionsFor = (phase, config = {}) =>
  BY_PHASE[phase]?.({ ...DEFAULT_CONFIG, ...withoutUndefined(config) }) ?? [];
